import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Image} from '@chakra-ui/react'
import {GoogleMap, Marker} from '@react-google-maps/api'
import {useIntl} from 'react-intl'
import {useGoogleMapsApiLoader} from '../../google-maps-api'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useCountry} from '../../hooks'
import {COUNTRY_MESSAGES} from '../../constants'
import useEffectEvent from '../../hooks/use-effect-event'

export default function MapPage({
    locations,
    activeMarker,
    dontFocusMarker,
    hideMarkerNumbers,
    selected: selectedProp,
    onSelectedChange
}) {
    const mapRef = useRef(null)
    const intl = useIntl()
    const ocapiSite = useOcapiSite()
    const ocapiSitePreferences = ocapiSite.preferences
    const {googleMapsApiKey} = ocapiSitePreferences

    const isControlled = selectedProp !== undefined && onSelectedChange !== undefined
    const [internalSelected, setInternalSelected] = useState(activeMarker)
    const selected = isControlled ? selectedProp : internalSelected
    const setSelected = isControlled ? onSelectedChange : setInternalSelected
    const [focusMarker, setFocusMarker] = useState(!dontFocusMarker)
    const [mapsApi, setMapsApi] = useState(false)

    const countryCode = useCountry()
    const countryText = intl.formatMessage(COUNTRY_MESSAGES[countryCode])

    const loadApi = useGoogleMapsApiLoader()

    useEffect(() => {
        if (mapsApi) return
        let unmounted = false
        loadApi().then((api) => {
            if (!unmounted) setMapsApi(api)
        })
        return () => {
            unmounted = true
        }
    }, [loadApi, mapsApi])

    const boundsToAllMarkers = useCallback(
        (map) => {
            const bounds = new window.google.maps.LatLngBounds()
            locations.forEach((loc) => bounds.extend(loc.position))
            map.fitBounds(bounds)
        },
        [locations]
    )

    const onLoad = useCallback(
        (map) => {
            mapRef.current = map
            if (!activeMarker) {
                boundsToAllMarkers(map)
            }
        },
        [activeMarker, boundsToAllMarkers]
    )

    useEffect(() => {
        if (!mapRef.current) return

        if (activeMarker) {
            mapRef.current.panTo(activeMarker.position)
            mapRef.current.setZoom(16)
            setSelected(activeMarker)
            setFocusMarker(!dontFocusMarker)
        } else {
            boundsToAllMarkers(mapRef.current)
            setSelected(null)
        }
    }, [activeMarker, boundsToAllMarkers, dontFocusMarker, setSelected])

    // Manually handle the info window instead of using the component, because
    // the component doesn't allow to set the `shouldFocus` prop on open,
    // which is conflicting with stores page search being focused and showing autocomplete
    const infoWindowRef = useRef(null)
    const onCloseClick = useEffectEvent(() => setSelected(null))
    useEffect(() => {
        if (!mapsApi) return
        if (!selected?.infoWindowHtml) return

        // reuse existing instance
        infoWindowRef.current = infoWindowRef.current || new mapsApi.InfoWindow({})

        // register close listener
        const listenerRef = mapsApi.event.addListener(
            infoWindowRef.current,
            'closeclick',
            onCloseClick
        )

        // reuse existing content element
        const content = infoWindowRef.current.getContent() || document.createElement('div')
        // set content html
        content.innerHTML = selected.infoWindowHtml
        infoWindowRef.current.setContent(content)

        // set position
        infoWindowRef.current.setPosition(selected.position)

        // open
        infoWindowRef.current.open({map: mapRef.current, shouldFocus: focusMarker})
        return () => {
            // remove listener
            mapsApi.event.removeListener(listenerRef)

            // cleanup the html content to free up memory
            content.innerHTML = ''

            // close the info window
            infoWindowRef.current.close()
        }
    }, [focusMarker, mapsApi, selected, onCloseClick])

    return (
        <Box>
            {!mapsApi || locations.length === 0 ? (
                <Image
                    src={
                        'https://maps.googleapis.com/maps/api/staticmap?center=' +
                        countryText +
                        '&zoom=5&size=500x300&key=' +
                        googleMapsApiKey
                    }
                    width="100%"
                    alt={intl.formatMessage(
                        {
                            defaultMessage: 'Placeholder map for {country}',
                            id: 'map.placeholder_country'
                        },
                        {
                            country: countryText
                        }
                    )}
                />
            ) : (
                <Box
                    role="region"
                    aria-label={intl.formatMessage({
                        defaultMessage: 'Map with all stores',
                        id: 'map.all_stores'
                    })}
                >
                    <GoogleMap
                        mapContainerStyle={{width: '100%', height: '31rem'}}
                        zoom={14}
                        onLoad={onLoad}
                    >
                        {locations.map((loc, index) => (
                            <Marker
                                key={loc.id}
                                position={loc.position}
                                onClick={() => {
                                    setSelected(loc)
                                    setFocusMarker(true)
                                }}
                                label={
                                    hideMarkerNumbers !== true
                                        ? {
                                              text: String(index + 1),
                                              color: 'white',
                                              fontSize: '16px',
                                              fontWeight: 'bold'
                                          }
                                        : undefined
                                }
                                title={
                                    intl.formatMessage({
                                        defaultMessage: 'Store: ',
                                        id: 'store_locator.store'
                                    }) + loc.name
                                }
                            />
                        ))}
                    </GoogleMap>
                </Box>
            )}
        </Box>
    )
}

MapPage.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            position: PropTypes.object.isRequired,
            infoWindowHtml: PropTypes.string
        })
    ).isRequired,
    activeMarker: PropTypes.object,
    dontFocusMarker: PropTypes.bool,
    hideMarkerNumbers: PropTypes.bool,
    selected: PropTypes.object,
    onSelectedChange: PropTypes.func
}
